import useScrollFade from '../../../hooks/useScrollFade';

const steps = [
  {
    number: '01',
    icon: 'ri-search-eye-line',
    title: 'Technology Assessment',
    description: 'Evaluate current systems, architecture, and development practices.',
    badgeBg: 'bg-teal-500',
    badgeShadow: 'group-hover:shadow-[0_0_20px_rgba(20,184,166,0.5)]',
    iconBg: 'bg-teal-50',
    iconBorder: 'border-teal-200',
    iconColor: 'text-teal-600',
    connectorColor: 'from-teal-300 to-teal-100',
    dotColor: 'bg-teal-400',
  },
  {
    number: '02',
    icon: 'ri-road-map-line',
    title: 'Strategic Roadmap',
    description: 'Define a technology strategy aligned with business objectives.',
    badgeBg: 'bg-amber-500',
    badgeShadow: 'group-hover:shadow-[0_0_20px_rgba(245,158,11,0.5)]',
    iconBg: 'bg-amber-50',
    iconBorder: 'border-amber-200',
    iconColor: 'text-amber-600',
    connectorColor: 'from-amber-300 to-amber-100',
    dotColor: 'bg-amber-400',
  },
  {
    number: '03',
    icon: 'ri-rocket-line',
    title: 'Implementation Leadership',
    description: 'Guide engineering teams and oversee critical architecture decisions.',
    badgeBg: 'bg-rose-500',
    badgeShadow: 'group-hover:shadow-[0_0_20px_rgba(244,63,94,0.5)]',
    iconBg: 'bg-rose-50',
    iconBorder: 'border-rose-200',
    iconColor: 'text-rose-600',
    connectorColor: 'from-rose-300 to-rose-100',
    dotColor: 'bg-rose-400',
  },
  {
    number: '04',
    icon: 'ri-line-chart-line',
    title: 'Ongoing Advisory',
    description: 'Provide continued technical leadership as your company grows.',
    badgeBg: 'bg-emerald-500',
    badgeShadow: 'group-hover:shadow-[0_0_20px_rgba(16,185,129,0.5)]',
    iconBg: 'bg-emerald-50',
    iconBorder: 'border-emerald-200',
    iconColor: 'text-emerald-600',
    connectorColor: 'from-emerald-300 to-emerald-100',
    dotColor: 'bg-emerald-400',
  },
];

const ProcessSection = () => {
  const header = useScrollFade();
  const step0 = useScrollFade({ delay: 0 });
  const step1 = useScrollFade({ delay: 100 });
  const step2 = useScrollFade({ delay: 200 });
  const step3 = useScrollFade({ delay: 300 });

  const stepRefs = [step0, step1, step2, step3];

  return (
    <section id="process" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div
          ref={header.ref}
          className={`text-center mb-16 ${header.fadeClass}`}
        >
          <h3 className="text-teal-600 text-sm font-bold uppercase tracking-wider mb-3">
            Engagement Process
          </h3>
          <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-4">
            How Fractional CTO Engagements Work
          </h2>
          <div className="flex items-center justify-center gap-2 mt-6">
            <div className="h-px w-16 bg-gray-200" />
            <div className="h-0.5 w-10 rounded-full bg-teal-400 shadow-[0_0_8px_rgba(20,184,166,0.6)]" />
            <div className="h-px w-16 bg-gray-200" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => {
            const { ref, fadeClass } = stepRefs[index];
            return (
              <div
                key={index}
                ref={ref}
                className={`relative ${fadeClass}`}
              >
                <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-8 border border-gray-100 shadow-sm hover:shadow-xl hover:shadow-gray-200/60 hover:-translate-y-1 group transition-all duration-300 h-full">
                  <div className={`absolute -top-4 -left-4 w-16 h-16 ${step.badgeBg} rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 ${step.badgeShadow} transition-all duration-300`}>
                    <span className="text-white text-xl font-bold">{step.number}</span>
                  </div>

                  <div className={`w-14 h-14 flex items-center justify-center rounded-2xl ${step.iconBg} border ${step.iconBorder} mb-6 mt-8 group-hover:scale-110 transition-all duration-300`}>
                    <i className={`${step.icon} text-2xl ${step.iconColor}`}></i>
                  </div>

                  <h4 className="text-lg font-bold text-gray-900 mb-3">{step.title}</h4>
                  <p className="text-gray-500 text-sm leading-relaxed">{step.description}</p>
                </div>

                {index < steps.length - 1 && (
                  <div className={`hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r ${step.connectorColor} -translate-y-1/2 z-10`}>
                    <div className={`absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 ${step.dotColor} rounded-full`} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;